// © 2025 Camp Promethean, LLC. All Rights Reserved.
// Selections API

import { supabase } from '@/lib/supabase';
import { Selection } from '@/types/selection';

/**
 * Fetches all selections for a project
 */
export async function getSelections(projectId: string) {
  const { data, error } = await supabase
    .from('selections')
    .select('*')
    .eq('project_id', projectId)
    .order('selection_date', { ascending: false });
  
  if (error) {
    throw new Error(`Error fetching selections: ${error.message}`);
  }
  
  return data;
}

/**
 * Fetches a single selection by ID
 */
export async function getSelection(id: string) {
  const { data, error } = await supabase
    .from('selections')
    .select('*')
    .eq('id', id)
    .single();
  
  if (error) {
    throw new Error(`Error fetching selection: ${error.message}`);
  }
  
  return data;
}

/**
 * Creates a new selection
 */
export async function createSelection(selection: Omit<Selection, 'id' | 'created_at' | 'updated_at'>) {
  const { data, error } = await supabase
    .from('selections')
    .insert(selection)
    .select()
    .single();
  
  if (error) {
    throw new Error(`Error creating selection: ${error.message}`);
  }
  
  return data;
}

/**
 * Updates an existing selection
 */
export async function updateSelection(selection: Partial<Selection> & { id: string }) {
  const { id, ...updateData } = selection;
  
  const { data, error } = await supabase
    .from('selections')
    .update(updateData)
    .eq('id', id)
    .select()
    .single();
  
  if (error) {
    throw new Error(`Error updating selection: ${error.message}`);
  }
  
  return data;
}

/**
 * Deletes a selection
 */
export async function deleteSelection(id: string) {
  const { error } = await supabase
    .from('selections')
    .delete()
    .eq('id', id);
  
  if (error) {
    throw new Error(`Error deleting selection: ${error.message}`);
  }
  
  return true;
}

/**
 * Fetches selection data with related plant and sensory data
 */
export async function getEnhancedSelection(id: string) {
  // First get the selection
  const { data: selection, error: selectionError } = await supabase
    .from('selections')
    .select('*')
    .eq('id', id)
    .single();
  
  if (selectionError) {
    throw new Error(`Error fetching selection: ${selectionError.message}`);
  }
  
  if (!selection) {
    throw new Error('Selection not found');
  }
  
  // Get associated sensory data if available
  const { data: sensoryData, error: sensoryError } = await supabase
    .from('phenos_sensory')
    .select('*')
    .eq('pheno_id', selection.pheno_id)
    .eq('plant_id', selection.plant_id)
    .maybeSingle();
  
  if (sensoryError && sensoryError.code !== 'PGRST116') { // Not found is ok
    throw new Error(`Error fetching sensory data: ${sensoryError.message}`);
  }
  
  // Get tags
  const { data: tags, error: tagsError } = await supabase
    .from('selection_tags')
    .select('*')
    .eq('selection_id', id);
  
  if (tagsError) {
    throw new Error(`Error fetching selection tags: ${tagsError.message}`);
  }
  
  return {
    ...selection,
    sensoryData: sensoryData || null,
    tags: tags || []
  };
}

/**
 * Fetches all selections for a project with enhanced data
 */
export async function getEnhancedSelections(projectId: string) {
  // First get all selections
  const { data: selections, error: selectionsError } = await supabase
    .from('selections')
    .select('*')
    .eq('project_id', projectId)
    .order('selection_date', { ascending: false });
  
  if (selectionsError) {
    throw new Error(`Error fetching selections: ${selectionsError.message}`);
  }
  
  if (!selections.length) {
    return [];
  }
  
  // Get all sensory data for these selections
  const phenoIds = [...new Set(selections.map(s => s.pheno_id))];
  const plantIds = [...new Set(selections.map(s => s.plant_id))];
  
  const { data: sensoryData, error: sensoryError } = await supabase
    .from('phenos_sensory')
    .select('*')
    .in('pheno_id', phenoIds)
    .in('plant_id', plantIds);
  
  if (sensoryError) {
    throw new Error(`Error fetching sensory data: ${sensoryError.message}`);
  }
  
  // Get all tags for these selections
  const selectionIds = selections.map(s => s.id);
  
  const { data: tags, error: tagsError } = await supabase
    .from('selection_tags')
    .select('*')
    .in('selection_id', selectionIds);
  
  if (tagsError) {
    throw new Error(`Error fetching selection tags: ${tagsError.message}`);
  }
  
  // Combine everything
  return selections.map(selection => {
    const matchingSensory = sensoryData?.find(
      s => s.pheno_id === selection.pheno_id && s.plant_id === selection.plant_id
    ) || null;
    
    const selectionTags = tags?.filter(
      t => t.selection_id === selection.id
    ) || [];
    
    return {
      ...selection,
      sensoryData: matchingSensory,
      tags: selectionTags
    };
  });
} 